import type { SingleBill } from '@ctct/domain-layer';
import type { SingleBillPayload } from '@ctct/infrastructure-layer';
import type { CreateSingleBillMutation, UpdateSingleBillMutation } from '@ctct/state-server-layer';

import type { TranslationGetter } from '../../../types';
import type { UpdateSingleBillPageStore } from '../types';
import { updateSingleBillEventBusEventBus } from '../UpdateSingleBillEventBus';

export class UpdateSingleBillService {
  constructor(
    private pageStore: UpdateSingleBillPageStore,
    private singleBill: SingleBill,
    private updateSingleBillMutation: UpdateSingleBillMutation,
    private createSingleBillMutation: CreateSingleBillMutation,
    private t: TranslationGetter
  ) {}

  public api() {
    return {
      reportBeginLoading: () => this.reportBeginLoading(),
      reportEndLoading: () => this.reportEndLoading(),
      reportSuccess: () => this.reportSuccess(),
      reportError: (error: Error) => this.reportError(error),
      getSuccessMessage: () => this.getSuccessfulUpdateMessage(),
      submitCreateSingleBill: (createSingleBillInfoPayload: SingleBillPayload) =>
        this.submitCreateSingleBill(createSingleBillInfoPayload),
      submitUpdateSingleBill: (updateSingleBillInfoPayload: SingleBillPayload) =>
        this.submitUpdateSingleBill(updateSingleBillInfoPayload),
      reportFormDirtyState: (isDirty: boolean) => this.pageStore.setState({ updateSingleBillIsFormChanged: isDirty }),
      reportPaymentCanBeUpdated: (isDirty: boolean) =>
        this.pageStore.setState({ updateSingleBillPaymentCanBeUpdated: isDirty }),
    };
  }

  public data() {
    return {
      getButtonLabel: () =>
        this.singleBill.getIsSingleBillActivated()
          ? this.t('application-layer.singlebill-page.save')
          : this.t('application-layer.singlebill-page.create'),
    };
  }

  public handlers() {
    return {
      onSubmitClicked: () => {
        if (this.singleBill.getIsSingleBillActivated()) {
          updateSingleBillEventBusEventBus.emit('requestSingleBillUpdate', { source: 'user' });
        } else {
          updateSingleBillEventBusEventBus.emit('requestSingleBillCreate', { source: 'user' });
        }
      },
    };
  }

  private reportBeginLoading() {
    this.pageStore.setState({
      isUpdateSingleBillLoading: true,
      isUpdateSingleBillSuccess: false,
      updateSingleBillError: null,
    });
  }

  private reportEndLoading() {
    this.pageStore.setState({
      isUpdateSingleBillLoading: false,
    });
  }

  private reportSuccess() {
    this.pageStore.setState({
      isUpdateSingleBillLoading: false,
      isUpdateSingleBillSuccess: true,
    });
  }

  private reportError(error: Error) {
    this.pageStore.setState({
      isUpdateSingleBillLoading: false,
      isUpdateSingleBillSuccess: false,
      updateSingleBillError: error,
    });
  }

  private getSuccessfulUpdateMessage() {
    return {
      header: this.t('application-layer.singlebill-page.alert-success-heading'),
      message: this.t('application-layer.singlebill-page.alert-success-message'),
    };
  }

  private async submitCreateSingleBill(createSingleBillInfoPayload: SingleBillPayload) {
    await this.createSingleBillMutation.mutateAsync(createSingleBillInfoPayload);
  }

  private async submitUpdateSingleBill(updateSingleBillInfoPayload: SingleBillPayload) {
    await this.updateSingleBillMutation.mutateAsync(updateSingleBillInfoPayload);
  }
}
